import React from 'react';
import { TableRow, TableCell, Typography, Button, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OverBudgetFlag from './OverBudgetFlag';
import ScheduleChip from './ScheduleChip';

const AMBER = '#BA7517';

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Format a number with comma separators and up to `digits` decimal places.
 */
const fmtHrs = (value, digits = 0) =>
  (value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });

/**
 * Format an ISO date string to compact display.
 * Shows "Apr 6" if current year, "Apr 6 '25" if different year.
 * Returns "—" if input is null/undefined.
 */
const fmtDate = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const month = d.toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  const currentYear = new Date().getFullYear();
  return year === currentYear
    ? `${month} ${day}`
    : `${month} ${day} '${String(year).slice(2)}`;
};

/**
 * Format a full display name to abbreviated form.
 * "John Peterson" → "J. Peterson"
 * Returns the input as-is if it can't be split.
 */
const abbreviateName = (fullName) => {
  if (!fullName) return null;
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  return `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
};

/**
 * Format the "Last Updated" timestamp to short date.
 * "2026-05-07T..." → "May 7"
 */
const fmtUpdatedDate = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ── Component ────────────────────────────────────────────────────────────

/**
 * Single row in the Work Package Progress table.
 *
 * Renders all thirteen columns per the v1.1 table column specification:
 * Package | PM | Schedule % | Actual % | % Earned | ⚑ | Planned Dates |
 * Schedule | Budget Hrs | Actual Hrs | Earned Hrs | Last Updated | Action
 *
 * Computes derived values locally (lane Actual %, earned hrs, over-budget flag).
 *
 * Shop/field stacking: Budget Hrs, Earned Hrs, Actual Hrs, and paired planned
 * dates when `requires_shop_fabrication` (requiresShop) is true — stack shop/field
 * even if shop budget is zero so unbudgeted shop actuals stay visible. Shop budget
 * and shop earned show `—` when there is no shop budget; shop actual hours always
 * use real values. Order and emphasis follow the parent section (shop vs field lane).
 * When requiresShop is false, show a single combined value / field dates only.
 *
 * Props
 * ─────
 * pkg             Object     full package object from the GET /packages response
 * onUpdateClick   Function   callback to open the Update Progress modal
 * isEvenRow       bool       alternating row background for readability
 * isField         bool       from parent section: false = shop prefab lane
 *                             (shop stacked values on top, larger); true =
 *                             field anchor lane (field on top, larger)
 */
const PackageTableRow = ({
  pkg,
  onUpdateClick,
  isEvenRow = false,
  isField = false,
}) => {
  // ── Derived values ──────────────────────────────────────────────────────
  const fieldBudget = pkg.fieldBudgetHrs || 0;
  const shopBudget = pkg.shopBudgetHrs || 0;
  const totalBudget = fieldBudget + shopBudget;

  const fieldActual = pkg.fieldActualHrs || 0;
  const shopActual = pkg.shopActualHrs || 0;
  const totalActual = fieldActual + shopActual;

  const fieldPct = pkg.fieldPercentComplete;
  const shopPct = pkg.shopPercentComplete;
  /** % earned column + over-budget flag: lane matches table (shop vs field). */
  const laneEarnedPct = isField ? fieldPct : shopPct;
  /** Stack shop/field whenever package requires shop fab (not gated on shop budget). */
  const showStacked = Boolean(pkg.requiresShop);
  const hasShopBudget = shopBudget > 0;

  // Actual % — shop lane: shop actual ÷ shop budget; field lane: field ÷ field (cap 999%)
  const actualPct = isField
    ? fieldBudget > 0
      ? Math.min((fieldActual / fieldBudget) * 100, 999)
      : null
    : shopBudget > 0
      ? Math.min((shopActual / shopBudget) * 100, 999)
      : null;

  // Earned hours — stored packages.earned_*_hrs (not budget × percent)
  const earnedFieldHrs = Number(pkg.fieldEarnedHrs) || 0;
  const earnedShopHrs = Number(pkg.shopEarnedHrs) || 0;
  const totalEarnedHrs = earnedFieldHrs + earnedShopHrs;

  const shopBudgetDisplay = hasShopBudget ? fmtHrs(shopBudget) : '—';
  const shopEarnedDisplay = hasShopBudget ? fmtHrs(earnedShopHrs) : '—';

  // Common cell styling
  const cellSx = {
    fontSize: '12px',
    py: 0.75,
    borderBottom: '0.5px solid',
    borderColor: 'divider',
  };

  const numCellSx = {
    ...cellSx,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  };

  // Pin the action cell to the right edge so the Update Progress button stays
  // reachable without scrolling the wide table all the way right on laptops.
  // A solid background (plus the stripe tint overlay on even rows) is required so
  // horizontally scrolled cells don't bleed through the pinned column.
  const stickyActionCellSx = {
    ...cellSx,
    textAlign: 'right',
    position: 'sticky',
    right: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    borderLeft: '0.5px solid',
    borderLeftColor: 'divider',
    ...(isEvenRow
      ? {
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.04))',
        }
      : {}),
  };

  // Shop prefab table: shop on top, larger; field anchor table: field on top, larger.
  // Same font weight as before (400); size only differentiates primary vs secondary.
  const stackPrimarySx = {
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.4,
    color: 'text.primary',
  };
  const stackSecondarySx = {
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: 1.4,
    color: 'text.secondary',
  };
  const stackTagSx = { color: 'text.secondary', fontSize: '10px' };

  // Labels only on the secondary line (matches old behavior: primary was field dates
  // with no tag; only the smaller shop row had a "shop" tag).
  const StackedValues = ({ shopVal, fieldVal }) => {
    const shopLine = (
      <Typography sx={isField ? stackSecondarySx : stackPrimarySx}>
        {shopVal}
        {isField ? (
          <>
            {' '}
            <Box component="span" sx={stackTagSx}>
              shop
            </Box>
          </>
        ) : null}
      </Typography>
    );
    const fieldLine = (
      <Typography sx={isField ? stackPrimarySx : stackSecondarySx}>
        {fieldVal}
        {!isField ? (
          <>
            {' '}
            <Box component="span" sx={stackTagSx}>
              field
            </Box>
          </>
        ) : null}
      </Typography>
    );
    return (
      <Box>
        {isField ? (
          <>
            {fieldLine}
            {shopLine}
          </>
        ) : (
          <>
            {shopLine}
            {fieldLine}
          </>
        )}
      </Box>
    );
  };

  const plannedPrimarySx = {
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.4,
    color: 'text.primary',
  };
  const plannedSecondarySx = {
    fontSize: '11px',
    lineHeight: 1.4,
    color: 'text.secondary',
  };
  const plannedTagSx = { fontSize: '10px' };

  // ── Last Updated formatting (shop lane → shop % meta; field lane → field) ─
  const updatedByName = abbreviateName(
    isField ? pkg.fieldPctUpdatedBy : pkg.shopPctUpdatedBy
  );
  const updatedDate = fmtUpdatedDate(
    isField ? pkg.fieldPctUpdatedAt : pkg.shopPctUpdatedAt
  );
  const neverUpdated = isField ? !pkg.fieldPctUpdatedAt : !pkg.shopPctUpdatedAt;

  return (
    <TableRow
      hover
      sx={{
        bgcolor: isEvenRow ? 'action.hover' : 'transparent',
        '&:hover': { bgcolor: 'action.selected' },
      }}
    >
      {/* ── Package name ────────────────────────────────────────── */}
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.3 }}>
          {pkg.autodeskName ? (
            <>
              {pkg.autodeskName}
              {' — '}
              {pkg.name}
            </>
          ) : (
            pkg.name
          )}
        </Typography>
      </TableCell>

      {/* ── PM ────────────────────────────────────────────────── */}
      <TableCell sx={cellSx}>
        <Typography
          sx={{
            fontSize: '12px',
            color: pkg.pmDisplayName ? 'text.primary' : 'text.disabled',
            whiteSpace: 'nowrap',
          }}
        >
          {pkg.pmDisplayName || '—'}
        </Typography>
      </TableCell>

      {/* ── Schedule % ───────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          {pkg.schedulePct != null ? `${pkg.schedulePct}%` : ''}
        </Typography>
      </TableCell>

      {/* ── Actual % ────────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          {actualPct != null ? `${actualPct.toFixed(1)}%` : '—'}
        </Typography>
      </TableCell>

      {/* ── % Earned ─────────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        <Typography sx={{ fontSize: '12px' }}>
          {laneEarnedPct != null ? `${laneEarnedPct}%` : '—'}
        </Typography>
      </TableCell>

      {/* ── ⚑ Over-budget flag ───────────────────────────────────── */}
      <TableCell sx={{ ...cellSx, textAlign: 'center', px: 0 }}>
        <OverBudgetFlag
          actualHrs={isField ? fieldActual : shopActual}
          budgetHrs={isField ? fieldBudget : shopBudget}
          percentEarned={laneEarnedPct ?? 0}
        />
      </TableCell>

      {/* ── Planned Dates ───────────────────────────────────────── */}
      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
        {showStacked && (pkg.shopSchedStart || pkg.shopSchedEnd) ? (
          isField ? (
            <>
              <Typography sx={plannedPrimarySx}>
                {fmtDate(pkg.schedStart)} — {fmtDate(pkg.schedEnd)}
              </Typography>
              <Typography sx={plannedSecondarySx}>
                {fmtDate(pkg.shopSchedStart)} — {fmtDate(pkg.shopSchedEnd)}{' '}
                <Box component="span" sx={plannedTagSx}>
                  shop
                </Box>
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={plannedPrimarySx}>
                {fmtDate(pkg.shopSchedStart)} — {fmtDate(pkg.shopSchedEnd)}
              </Typography>
              <Typography sx={plannedSecondarySx}>
                {fmtDate(pkg.schedStart)} — {fmtDate(pkg.schedEnd)}{' '}
                <Box component="span" sx={plannedTagSx}>
                  field
                </Box>
              </Typography>
            </>
          )
        ) : (
          <Typography sx={{ fontSize: '12px', lineHeight: 1.4 }}>
            {fmtDate(pkg.schedStart)} — {fmtDate(pkg.schedEnd)}
          </Typography>
        )}
      </TableCell>

      {/* ── Schedule chip (field finish vs shop finish by lane) ─── */}
      <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
        <ScheduleChip
          plannedFinishDate={isField ? pkg.schedEnd : pkg.shopSchedEnd}
          isComplete={
            isField
              ? Boolean(pkg.fieldActualFinishDate)
              : Boolean(pkg.shopActualFinishDate)
          }
        />
      </TableCell>

      {/* ── Budget Hrs ──────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        {showStacked ? (
          <StackedValues
            shopVal={shopBudgetDisplay}
            fieldVal={fmtHrs(fieldBudget)}
          />
        ) : (
          <Typography sx={{ fontSize: '12px' }}>
            {fmtHrs(totalBudget)}
          </Typography>
        )}
      </TableCell>

      {/* ── Actual Hrs ──────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        {showStacked ? (
          <StackedValues
            shopVal={fmtHrs(shopActual)}
            fieldVal={fmtHrs(fieldActual)}
          />
        ) : (
          <Typography sx={{ fontSize: '12px' }}>
            {fmtHrs(totalActual)}
          </Typography>
        )}
      </TableCell>

      {/* ── Earned Hrs ──────────────────────────────────────────── */}
      <TableCell sx={numCellSx}>
        {showStacked ? (
          <StackedValues
            shopVal={shopEarnedDisplay}
            fieldVal={fmtHrs(earnedFieldHrs)}
          />
        ) : (
          <Typography sx={{ fontSize: '12px' }}>
            {fmtHrs(totalEarnedHrs)}
          </Typography>
        )}
      </TableCell>

      {/* ── Last Updated ────────────────────────────────────────── */}
      <TableCell sx={cellSx}>
        {neverUpdated ? (
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: AMBER,
              whiteSpace: 'nowrap',
            }}
          >
            ⚠ Not yet updated
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {updatedByName} · {updatedDate}
          </Typography>
        )}
      </TableCell>

      {/* ── Action (pinned right) ───────────────────────────────── */}
      <TableCell sx={stickyActionCellSx}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          onClick={() => onUpdateClick(pkg)}
          sx={{
            fontSize: '11px',
            py: 0.25,
            px: 1.25,
            textTransform: 'none',
            borderColor: 'info.light',
            color: 'info.main',
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': { mr: 0.5 },
          }}
        >
          Update Progress
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default PackageTableRow;
